import { api } from './apiClient';

/**
 * Meditation Admin API — `/api/v1/admin/meditation`
 * Contract: docs/meditation-admin/MEDITATION_ADMIN_GUIDE.md (aligned with Vedify-Backend routes/admin/v1.js)
 *
 * Pass request bodies through as supplied. Do not strip inactive Session refs or invent pagination.
 * HTTP 503 on Main cascade remains a rejected promise (err.status === 503, err.payload preserved).
 */

const BASE = '/api/v1/admin/meditation';

export const formatMeditationError = (error, fallback = 'Request failed') => {
  const data = error?.response?.data;
  if (!data) {
    if (error?.request) {
      return 'Unable to reach the server. Please check your internet connection or try again later.';
    }
    return error?.message || fallback;
  }
  if (Array.isArray(data.errors) && data.errors.length) {
    const head = data.message || 'Validation failed';
    return [head, ...data.errors].join('\n');
  }
  if (typeof data.errors === 'string' && data.errors) {
    return data.message && data.message !== data.errors
      ? `${data.message}\n${data.errors}`
      : data.errors;
  }
  return data.message || fallback;
};

const request = async (fn, fallback) => {
  try {
    const response = await fn();
    return response.data;
  } catch (error) {
    const err = new Error(formatMeditationError(error, fallback));
    err.payload = error?.response?.data;
    err.status = error?.response?.status;
    throw err;
  }
};

const pathId = (id) => encodeURIComponent(String(id));

// ---------------------------------------------------------------------------
// Main Categories
// ---------------------------------------------------------------------------

/** POST /meditation/main-categories */
export const createMeditationMainCategory = async (payload) =>
  request(
    () => api.post(`${BASE}/main-categories`, payload),
    'Failed to create meditation main category'
  );

/** PUT /meditation/main-categories — body includes main_category_id */
export const updateMeditationMainCategory = async (payload) =>
  request(
    () => api.put(`${BASE}/main-categories`, payload),
    'Failed to update meditation main category'
  );

/** POST /meditation/main-categories/list — no pagination */
export const listMeditationMainCategories = async (body = {}) =>
  request(
    () => api.post(`${BASE}/main-categories/list`, body),
    'Failed to load meditation main categories'
  );

/** POST /meditation/main-categories/with-subcategories — nested tree list */
export const listMeditationMainCategoriesWithSubcategories = async (body = {}) =>
  request(
    () => api.post(`${BASE}/main-categories/with-subcategories`, body),
    'Failed to load meditation main categories with subcategories'
  );

/** GET /meditation/main-categories/:main_category_id */
export const getMeditationMainCategory = async (mainCategoryId) =>
  request(
    () => api.get(`${BASE}/main-categories/${pathId(mainCategoryId)}`),
    'Failed to load meditation main category'
  );

/** DELETE /meditation/main-categories/:main_category_id — soft delete + cascade (may 503) */
export const deleteMeditationMainCategory = async (mainCategoryId) =>
  request(
    () => api.delete(`${BASE}/main-categories/${pathId(mainCategoryId)}`),
    'Failed to delete meditation main category'
  );

/** PUT /meditation/main-categories/reorder — body: { categories: [{ main_category_id, order }] } */
export const reorderMeditationMainCategories = async (payload) =>
  request(
    () => api.put(`${BASE}/main-categories/reorder`, payload),
    'Failed to reorder meditation main categories'
  );

// ---------------------------------------------------------------------------
// Subcategories
// ---------------------------------------------------------------------------

/** POST /meditation/sub-categories */
export const createMeditationSubCategory = async (payload) =>
  request(
    () => api.post(`${BASE}/sub-categories`, payload),
    'Failed to create meditation subcategory'
  );

/** PUT /meditation/sub-categories — body includes sub_category_id; optional main_category_id to move */
export const updateMeditationSubCategory = async (payload) =>
  request(
    () => api.put(`${BASE}/sub-categories`, payload),
    'Failed to update meditation subcategory'
  );

/** POST /meditation/sub-categories/list — optional main_category_id filter (controller workaround) */
export const listMeditationSubCategories = async (body = {}) =>
  request(
    () => api.post(`${BASE}/sub-categories/list`, body),
    'Failed to load meditation subcategories'
  );

/** GET /meditation/sub-categories/:sub_category_id */
export const getMeditationSubCategory = async (subCategoryId) =>
  request(
    () => api.get(`${BASE}/sub-categories/${pathId(subCategoryId)}`),
    'Failed to load meditation subcategory'
  );

/** DELETE /meditation/sub-categories/:sub_category_id — soft delete */
export const deleteMeditationSubCategory = async (subCategoryId) =>
  request(
    () => api.delete(`${BASE}/sub-categories/${pathId(subCategoryId)}`),
    'Failed to delete meditation subcategory'
  );

/**
 * PUT /meditation/sub-categories/reorder
 * Body: { main_category_id, subcategories: [{ sub_category_id, order }] }
 */
export const reorderMeditationSubCategories = async (payload) =>
  request(
    () => api.put(`${BASE}/sub-categories/reorder`, payload),
    'Failed to reorder meditation subcategories'
  );

// ---------------------------------------------------------------------------
// Program Categories
// ---------------------------------------------------------------------------

/** POST /meditation/program-categories */
export const createMeditationProgramCategory = async (payload) =>
  request(
    () => api.post(`${BASE}/program-categories`, payload),
    'Failed to create meditation program category'
  );

/** PUT /meditation/program-categories — body includes program_category_id */
export const updateMeditationProgramCategory = async (payload) =>
  request(
    () => api.put(`${BASE}/program-categories`, payload),
    'Failed to update meditation program category'
  );

/** POST /meditation/program-categories/list — no pagination */
export const listMeditationProgramCategories = async (body = {}) =>
  request(
    () => api.post(`${BASE}/program-categories/list`, body),
    'Failed to load meditation program categories'
  );

/** GET /meditation/program-categories/:program_category_id */
export const getMeditationProgramCategory = async (programCategoryId) =>
  request(
    () => api.get(`${BASE}/program-categories/${pathId(programCategoryId)}`),
    'Failed to load meditation program category'
  );

/** DELETE /meditation/program-categories/:program_category_id — soft delete */
export const deleteMeditationProgramCategory = async (programCategoryId) =>
  request(
    () => api.delete(`${BASE}/program-categories/${pathId(programCategoryId)}`),
    'Failed to delete meditation program category'
  );

/**
 * PUT /meditation/program-categories/reorder
 * Body: { program_categories: [{ program_category_id, order }] }
 */
export const reorderMeditationProgramCategories = async (payload) =>
  request(
    () => api.put(`${BASE}/program-categories/reorder`, payload),
    'Failed to reorder meditation program categories'
  );

// ---------------------------------------------------------------------------
// Sessions
// ---------------------------------------------------------------------------

/** POST /meditation/sessions */
export const createMeditationSession = async (payload) =>
  request(
    () => api.post(`${BASE}/sessions`, payload),
    'Failed to create meditation session'
  );

/** PUT /meditation/sessions — body includes session_id; omitted arrays are preserved server-side */
export const updateMeditationSession = async (payload) =>
  request(
    () => api.put(`${BASE}/sessions`, payload),
    'Failed to update meditation session'
  );

/**
 * POST /meditation/sessions/list
 * Body may include: searchtext, is_active, sub_category_id, program_category_id,
 * pagination: { page, pageSize } (defaults applied by backend).
 */
export const listMeditationSessions = async (body = {}) =>
  request(
    () => api.post(`${BASE}/sessions/list`, body),
    'Failed to load meditation sessions'
  );

/** GET /meditation/sessions/:session_id — enriched */
export const getMeditationSession = async (sessionId) =>
  request(
    () => api.get(`${BASE}/sessions/${pathId(sessionId)}`),
    'Failed to load meditation session'
  );

/** DELETE /meditation/sessions/:session_id — soft delete */
export const deleteMeditationSession = async (sessionId) =>
  request(
    () => api.delete(`${BASE}/sessions/${pathId(sessionId)}`),
    'Failed to delete meditation session'
  );
